'use client'

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import instance from '@/lib/axiosInstance';
import { User } from '@/types/User';

interface AuthContextType {
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (accessToken: string, refreshToken?: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize auth on client
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        let accessToken = localStorage.getItem('access_token');

        if (!accessToken) {
          // No access token — try to restore session via refresh token (body or HttpOnly cookie)
          const storedRefreshToken = localStorage.getItem('refresh_token');
          try {
            const res = await axios.post(
              `${process.env.NEXT_PUBLIC_API_URL}/auth/refresh`,
              storedRefreshToken ? { refresh_token: storedRefreshToken } : {},
              { withCredentials: true }
            );
            const newAccessToken = res.data.access_token;
            const newRefreshToken = res.data.refresh_token;
            if (!newAccessToken) return;
            localStorage.setItem('access_token', newAccessToken);
            if (newRefreshToken) localStorage.setItem('refresh_token', newRefreshToken);
            accessToken = newAccessToken;
          } catch {
            return;
          }
        }

        const response = await instance.get('/auth/me');
        if (response.data?.data) {
          setUser(response.data.data);
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (accessToken: string, refreshToken?: string) => {
    localStorage.setItem('access_token', accessToken);
    if (refreshToken) {
      localStorage.setItem('refresh_token', refreshToken);
    }

    // Fetch user once
    const userResponse = await instance.get('/auth/me');
    setUser(userResponse.data.data);
    setIsAuthenticated(true);
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
    setIsAuthenticated(false);
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider value={{ user, setUser, isAuthenticated, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
